import { useCallback, useRef, useState } from "react";
import { LISTING_PLACEHOLDER_IMAGE } from "../../constants/automapListingConstants";
import {
  DEALER_LISTING_PHOTO_LIMITS,
  createListingPhotoDataUrlFromFile,
  validateDealerListingPhotoSelection,
} from "../../utils/dealerPortal/dealerListingPhotoFiles";

function createPhotoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createListingPhotoEntry(url = "") {
  return { id: createPhotoId(), url };
}

export function listingPhotosToEntries(photos = []) {
  if (!photos.length) return [];
  return photos.map((photo) => createListingPhotoEntry(photo.url || ""));
}

function isValidImageUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("data:image/")) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function movePhoto(entries, fromIndex, toIndex) {
  if (toIndex < 0 || toIndex >= entries.length || fromIndex === toIndex) {
    return entries;
  }
  const next = [...entries];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

/**
 * @param {{
 *   photos: { id: string, url: string }[],
 *   onChange: (photos: { id: string, url: string }[]) => void,
 *   disabled?: boolean,
 *   labels: Record<string, string>,
 * }} props
 */
export default function DealerListingPhotoManager({ photos, onChange, disabled = false, labels }) {
  const fileInputRef = useRef(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [urlError, setUrlError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);

  const validationMessages = {
    tooMany: labels.tooMany,
    tooLarge: labels.tooLarge,
    invalidType: labels.invalidType,
    empty: labels.emptyFile,
  };

  const addUrl = useCallback(() => {
    const trimmed = urlDraft.trim();
    if (!trimmed) return;
    if (!isValidImageUrl(trimmed)) {
      setUrlError(labels.invalidUrl);
      return;
    }
    setUrlError("");
    onChange([...photos, createListingPhotoEntry(trimmed)]);
    setUrlDraft("");
  }, [labels.invalidUrl, onChange, photos, urlDraft]);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const validation = validateDealerListingPhotoSelection(files, photos.length, validationMessages);
      if (!validation.ok) {
        setUploadError(validation.message);
        return;
      }

      setUploading(true);
      setUploadError("");

      try {
        const entries = [];
        for (const file of files) {
          const dataUrl = await createListingPhotoDataUrlFromFile(file);
          entries.push(createListingPhotoEntry(dataUrl));
        }
        onChange([...photos, ...entries]);
      } catch {
        setUploadError(labels.uploadFailed || labels.invalidType);
      } finally {
        setUploading(false);
      }
    },
    [labels.invalidType, labels.uploadFailed, onChange, photos, validationMessages]
  );

  const updateUrl = useCallback(
    (id, url) => {
      onChange(photos.map((photo) => (photo.id === id ? { ...photo, url } : photo)));
    },
    [onChange, photos]
  );

  const removePhoto = useCallback(
    (id) => {
      onChange(photos.filter((photo) => photo.id !== id));
    },
    [onChange, photos]
  );

  const setPrimary = useCallback(
    (index) => {
      if (index <= 0) return;
      onChange(movePhoto(photos, index, 0));
    },
    [onChange, photos]
  );

  const moveUp = useCallback(
    (index) => {
      onChange(movePhoto(photos, index, index - 1));
    },
    [onChange, photos]
  );

  const moveDown = useCallback(
    (index) => {
      onChange(movePhoto(photos, index, index + 1));
    },
    [onChange, photos]
  );

  const filledPhotos = photos.filter((photo) => photo.url.trim());
  const atPhotoLimit = photos.length >= DEALER_LISTING_PHOTO_LIMITS.maxFiles;

  return (
    <div className="dealer-photo-manager">
      <p className="dealer-photo-manager__helper muted">{labels.helper}</p>
      <p className="dealer-photo-manager__count">{labels.count.replace("{count}", String(filledPhotos.length))}</p>

      {photos.length ? (
        <ul className="dealer-photo-manager__grid" aria-label={labels.sectionsPhotos || "Fotos"}>
          {photos.map((photo, index) => (
            <li key={photo.id} className="dealer-photo-manager__item">
              <div className="dealer-photo-manager__preview">
                <img
                  src={photo.url.trim() || LISTING_PLACEHOLDER_IMAGE}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={(event) => {
                    event.currentTarget.src = LISTING_PLACEHOLDER_IMAGE;
                  }}
                />
                {index === 0 ? (
                  <span className="dealer-photo-manager__primary-badge">{labels.primaryBadge}</span>
                ) : null}
              </div>
              <input
                type="url"
                className="dealer-photo-manager__url-input"
                value={photo.url.startsWith("data:") ? "" : photo.url}
                disabled={disabled || photo.url.startsWith("data:")}
                placeholder={photo.url.startsWith("data:") ? labels.uploadedFilePlaceholder : labels.addUrlPlaceholder}
                onChange={(event) => updateUrl(photo.id, event.target.value)}
              />
              {photo.url.startsWith("data:") ? (
                <p className="dealer-photo-manager__file-note muted">{labels.uploadedFileNote}</p>
              ) : null}
              <div className="dealer-photo-manager__actions">
                <button
                  type="button"
                  className="dealer-portal-btn"
                  disabled={disabled || index === 0}
                  onClick={() => moveUp(index)}
                  title={labels.moveUp}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="dealer-portal-btn"
                  disabled={disabled || index === photos.length - 1}
                  onClick={() => moveDown(index)}
                  title={labels.moveDown}
                >
                  ↓
                </button>
                {index !== 0 ? (
                  <button
                    type="button"
                    className="dealer-portal-btn"
                    disabled={disabled}
                    onClick={() => setPrimary(index)}
                    title={labels.setPrimary}
                  >
                    ★
                  </button>
                ) : null}
                <button
                  type="button"
                  className="dealer-portal-btn"
                  disabled={disabled}
                  onClick={() => removePhoto(photo.id)}
                >
                  {labels.remove}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="dealer-photo-manager__empty">
          <p>{labels.empty}</p>
        </div>
      )}

      <div className="dealer-photo-manager__upload-row">
        <button
          type="button"
          className="dealer-portal-btn primary"
          disabled={disabled || uploading || atPhotoLimit}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? labels.uploadingFiles : labels.uploadFiles}
        </button>
        <p className="dealer-photo-manager__hint muted">{labels.uploadHint}</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={DEALER_LISTING_PHOTO_LIMITS.accept}
        multiple
        className="dealer-photo-manager__file-input"
        disabled={disabled || uploading || atPhotoLimit}
        onChange={(event) => {
          void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />

      <div className="dealer-photo-manager__add-row">
        <span className="dealer-photo-manager__add-label">{labels.orAddUrl}</span>
        <input
          type="url"
          value={urlDraft}
          disabled={disabled || atPhotoLimit}
          placeholder={labels.addUrlPlaceholder}
          onChange={(event) => {
            setUrlDraft(event.target.value);
            if (urlError) setUrlError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addUrl();
            }
          }}
        />
        <button type="button" className="dealer-portal-btn" disabled={disabled || atPhotoLimit} onClick={addUrl}>
          {labels.addUrl}
        </button>
      </div>

      {urlError ? <p className="dealer-photo-manager__error">{urlError}</p> : null}
      {uploadError ? <p className="dealer-photo-manager__error">{uploadError}</p> : null}
    </div>
  );
}
